import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  Drawer,
  Divider,
  IconButton,
  Snackbar,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppHeader from "../components/AppHeader";
import { formatDDMMYYYY, formatYangon } from "../services/datetimeService";

export default function ApprovalCenter({ setIsLoggedIn }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "error"
  });

  const tenant = localStorage.getItem("tenant") || "";

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL1}/approval/inbox`,
        {
          withCredentials: true,
          headers: { "X-Tenant": tenant }
        }
      );

      if (res.data.success) {
        setItems(res.data.items);
      }
    } catch (err) {
      console.error("Approval load error:", err);
    }
    setLoading(false);
  }, [tenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDetail = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };
  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const showError = (message) => {
    setSnack({ open: true, message, severity: "error" });
  };

  const closeSnack = () => {
    setSnack({ ...snack, open: false });
  };

  const handleAction = async (id, type, action) => {
    try {
      const endpoint =
        type === "manual_attendance"
          ? `/approval/${action}`
          : type === "ot_request"
          ? `/overtime/${action}`
          : action === "approve"
          ? `/device/hr-approve-otp`
          : `/device/hr-reject-otp`;

      const itemObj = items.find((x) => x.id === id && x.type === type);
      const payload =
        type === "device_change"
          ? { otp_request_id: id, action }
          : type === "ot_request"
          ? { id: id, action, approved_hours: itemObj?.requested_hours }
          : { request_id: id, action };
        
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}${endpoint}`,
        payload,
        {
          withCredentials: true,
          headers: { "X-Tenant": tenant }
        }
      );

      console.log("Approval result:", res.data);
      if (res.data?.success === false) {
        showError(res.data.message || "Action not allowed.");
        return;
      }
      loadData(); // refresh list
    } catch (err) {
      console.error("Action error:", err);
      const msg =
        err?.response?.data?.message ||
        "Action failed. Please try again.";
      showError(msg);
    }
  };

  return (
    <Box>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
          Approval Center
        </Typography>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Table
            sx={{
              "& .MuiTableRow-root": { height: 36 },
              "& .MuiTableCell-root": { padding: "4px 8px" }
            }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Staff Name</TableCell>
                  <TableCell>Requested Date</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Chip
                        label={
                          r.type === "manual_attendance"
                            ? "Manual Attendance"
                            : r.type === "ot_request"
                            ? "OT Request"
                            : "Device Change"
                        }
                        color={
                          r.type === "manual_attendance"
                            ? "warning"
                            : r.type === "ot_request"
                            ? "info"
                            : "secondary"
                        }
                      />
                    </TableCell>

                    <TableCell>{r.staff_name}</TableCell>
                    <TableCell>{formatDDMMYYYY(r.date) || "-"}</TableCell>
                    <TableCell>{r.reason}</TableCell>
                    <TableCell>{formatYangon(r.submitted_at)}</TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                        <Chip
                          size="small"
                          label={
                            r.status === "pending"
                              ? r.workflow_step === 2
                                ? "Pending HR"
                                : "Pending Manager"
                              : r.status
                          }
                          color={
                            r.status === "pending"
                              ? r.workflow_step === 2
                                ? "info"
                                : "warning"
                              : r.status === "approved"
                              ? "success"
                              : "error"
                          }
                        />
                        {r.manager_name && (
                          <Typography variant="caption" sx={{ color: "success.main", fontWeight: 700 }}>
                            Mgr: {r.manager_name} ✓
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => openDetail(r)}
                      >
                        View
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        sx={{ mr: 1 }}
                        onClick={() => handleAction(r.id, r.type, "approve")}
                      >
                        Approve
                      </Button>

                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleAction(r.id, r.type, "reject")}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={closeDetail}
        PaperProps={{ sx: { width: 420, p: 2 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Approval Details
          </Typography>
          <IconButton onClick={closeDetail}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {selected && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Type */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}
            >
              <Typography variant="subtitle2">Type :</Typography>
              <Box  sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}
              >
                <Chip
                  size="small"
                  label={
                    selected.type === "manual_attendance"
                      ? "Manual Attendance"
                      : selected.type === "ot_request"
                      ? "OT Request"
                      : "Device Change"
                  }
                  color={
                    selected.type === "manual_attendance"
                      ? "warning"
                      : selected.type === "ot_request"
                      ? "info"
                      : "secondary"
                  }
                />
              </Box>
            </Box>

            {/* Staff */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}
            >
              <Typography variant="subtitle2">Staff :</Typography>
              <Typography>{selected.staff_name}</Typography>
            </Box>


            {/* Requested Date */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}
            >
              <Typography variant="subtitle2">Requested Date :</Typography>
              <Typography>{formatDDMMYYYY(selected.date) || "-"}</Typography>
            </Box>

            {selected.type === "manual_attendance" && (
              <>
                {/* Requested Clock In */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    alignItems: "center",
                    columnGap: 1
                  }}
                >
                  <Typography variant="subtitle2">Requested In :</Typography>
                  <Typography
                    sx={{
                      color: "error.main",
                      fontWeight: 600
                    }}
                  >
                    {selected.requested_clock_in_time
                      ? formatYangon(selected.requested_clock_in_time)
                      : "-"}
                  </Typography>
                </Box>

                {/* Requested Clock Out */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    alignItems: "center",
                    columnGap: 1
                  }}
                >
                  <Typography variant="subtitle2">Requested Out :</Typography>
                  <Typography
                    sx={{
                      color: "error.main",
                      fontWeight: 600
                    }}
                  >
                    {selected.requested_clock_out_time
                      ? formatYangon(selected.requested_clock_out_time)
                      : "-"}
                  </Typography>
                </Box>
              </>
            )}

            {/* OT-specific fields */}
            {selected.type === "ot_request" && (
              <>
                {/* Clock In */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    alignItems: "center",
                    columnGap: 1
                  }}
                >
                  <Typography variant="subtitle2">Clock In :</Typography>
                  <Typography
                  sx={{
                    color: "error.main",
                    fontWeight: 600}}
                  >{formatYangon(selected.clock_in_time) || "-"}</Typography>
                </Box>

                {/* Clock Out */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    alignItems: "center",
                    columnGap: 1
                  }}
                >
                  <Typography variant="subtitle2">Clock Out :</Typography>
                  <Typography
                  sx={{
                    color: "error.main",
                    fontWeight: 600}}
                  >{formatYangon(selected.clock_out_time) || "-"}</Typography>
                </Box>

                {/* OT Hours Requested */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    alignItems: "center",
                    columnGap: 1
                  }}
                >
                  <Typography variant="subtitle2">OT Hours :</Typography>
                  <Typography  
                    sx={{
                    color: "red",
                    fontWeight: 600}}
                  >
                    {selected.requested_hours ?? "-"}
                  </Typography>
                </Box>
              </>
            )}

            {/* Status */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}
            >
              <Typography variant="subtitle2">Status :</Typography>
              <Box>
                <Chip
                  size="small"
                  label={
                    selected.status === "pending"
                      ? selected.workflow_step === 2
                        ? "Pending HR Approval"
                        : "Pending Manager Approval"
                      : selected.status
                  }
                  color={
                    selected.status === "pending"
                      ? selected.workflow_step === 2
                        ? "info"
                        : "warning"
                      : selected.status === "approved"
                      ? "success"
                      : "error"
                  }
                />
              </Box>
            </Box>

            {/* Workflow / Manager Approval Info Box */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: selected.manager_name ? "#f0fdf4" : "#f8fafc",
                borderColor: selected.manager_name ? "#bbf7d0" : "#e2e8f0",
                borderRadius: 2,
                mt: 1,
                mb: 1
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: selected.manager_name ? "#166534" : "text.primary" }}>
                Approval Progress
              </Typography>
              {selected.manager_name ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: "#15803d", fontWeight: 700 }}>
                    ✓ Step 1: Manager Approved
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Approved by: <strong>{selected.manager_name}</strong>
                  </Typography>
                  {selected.manager_approved_at && (
                    <Typography variant="caption" color="text.secondary">
                      Approved at: {formatYangon(selected.manager_approved_at)}
                    </Typography>
                  )}
                  {selected.manager_comment && (
                    <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary", mt: 0.5 }}>
                      Note: "{selected.manager_comment}"
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700 }}>
                    ➜ Step 2: Awaiting HR Final Approval
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {selected.status === "pending"
                    ? "⏳ Step 1: Awaiting Manager Approval"
                    : "No previous approval steps"}
                </Typography>
              )}
            </Paper>

            {/* Submitted */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}
            >
              <Typography variant="subtitle2">Submitted :</Typography>
              <Typography>{formatYangon(selected.submitted_at)}</Typography>
            </Box>

            {/* Reason */}
            {selected.reason && (
              <Box  
                sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                alignItems: "center",
                columnGap: 1
              }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Reason :
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {selected.reason}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Action Buttons inside Drawer */}
            {selected.status === "pending" && (
              <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="medium"
                  onClick={() => {
                    handleAction(selected.id, selected.type, "approve");
                    closeDetail();
                  }}
                >
                  Approve
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  size="medium"
                  onClick={() => {
                    handleAction(selected.id, selected.type, "reject");
                    closeDetail();
                  }}
                >
                  Reject
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Drawer>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}